import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Phone, MessageCircle, ArrowRight, Check, ShieldCheck, Clock } from "lucide-react";
import { Footer } from "@/components/Footer";
import { ChatWidget } from "@/components/ChatWidget";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Reveal, MaskedLine } from "@/lib/Reveal";
import { BUSINESS, SERVICES, COVERAGE } from "@/lib/data";

const pin = L.divIcon({
  className: "",
  html: '<div style="width:14px;height:14px;background:#FF5500;border-radius:9999px;box-shadow:0 0 16px rgba(255,85,0,0.9);border:2px solid #0A0A0A"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

export default function TownPage() {
  const { slug } = useParams();
  const town = COVERAGE.find((t) => t.slug === slug);

  useEffect(() => {
    if (!town) return;
    document.title = `Plumber & Heating Engineer in ${town.name} | Teign Gas`;
    const d = document.querySelector('meta[name="description"]');
    if (d) d.setAttribute("content", `Gas Safe plumber & heating engineer in ${town.name} — boiler servicing £85.99, 24/7 breakdowns, general plumbing (toilets, basins, sinks, taps) and new boilers from £1,799. Book & pay online with Teign Gas.`);
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "town-schema";
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "serviceType": "Plumbing and heating engineer",
      "provider": {
        "@type": ["Plumber", "HVACBusiness"],
        "name": "Teign Gas",
        "telephone": "+447737016265",
        "url": "https://teigngas.com/",
      },
      "areaServed": { "@type": "City", "name": town.name },
      "name": `Plumber & Heating Engineer in ${town.name}`,
      "description": `Gas Safe boiler servicing, 24/7 breakdowns, gas fires, cookers, general plumbing and new boilers in ${town.name}, South Devon. Fixed prices, online booking.`,
    });
    const bc = document.createElement("script");
    bc.type = "application/ld+json";
    bc.id = "town-bc-schema";
    bc.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://teigngas.com/" },
        { "@type": "ListItem", "position": 2, "name": "Areas We Cover", "item": "https://teigngas.com/#coverage" },
        { "@type": "ListItem", "position": 3, "name": `Plumber & Heating Engineer in ${town.name}` },
      ],
    });
    document.head.appendChild(schema);
    document.head.appendChild(bc);
    return () => { schema.remove(); bc.remove(); };
  }, [town]);

  if (!town) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-display font-extrabold text-2xl text-white uppercase">Area not found</h1>
          <Link to="/" className="mt-6 inline-block bg-torch text-white font-display font-bold uppercase px-7 py-3 rounded-sm">Back to homepage</Link>
        </div>
      </div>
    );
  }

  const others = COVERAGE.filter((t) => t.slug !== slug);

  return (
    <div data-testid="town-page" className="bg-obsidian text-neutral-100 overflow-x-clip">
      <div className="grain-overlay" />
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/" data-testid="town-logo" className="flex items-center shrink-0">
            <img src="/images/logo-flame.png" alt="Teign Gas" className="h-9 w-9 sm:hidden mix-blend-screen" />
            <img src="/images/logo-mark.png" alt="Teign Gas — Service, Repair, Install" className="hidden sm:block h-9 w-auto mix-blend-screen" />
          </Link>
          <div className="flex items-center gap-3">
            <a href={BUSINESS.phoneHref} data-testid="town-phone-link" className="hidden sm:flex items-center gap-2 font-mono text-xs tracking-wider text-neutral-300 border border-neutral-700 px-4 py-2.5 rounded-sm hover:border-torch hover:text-torch transition-colors">
              <Phone className="w-3.5 h-3.5" /> {BUSINESS.phone}
            </a>
            <Link to="/#booking" data-testid="town-book-btn" className="bg-torch hover:bg-torch-hover text-white font-display font-bold text-sm uppercase tracking-wide px-5 py-2.5 rounded-sm transition-all">
              Book Online
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-torch font-semibold">Plumber & heating engineer</p>
            <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-7xl text-white mt-4">
              <MaskedLine delay={0.1}>Plumbing & heating</MaskedLine>
              <MaskedLine delay={0.25}>
                <span className="text-torch">in {town.name}.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.45}>
              <p className="mt-7 max-w-2xl text-base sm:text-lg text-neutral-400 leading-relaxed">
                Teign Gas is your local Gas Safe registered plumber and heating engineer in {town.name}.
                Boiler servicing and 24/7 breakdowns, gas fires, cookers and hobs — plus all general
                plumbing: toilets that won't flush, basins, sinks, taps, leaks, bursts and new radiators.
                Fixed prices, booked and paid online in under two minutes.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link to="/#booking" data-testid="town-hero-book-btn" className="group bg-torch hover:bg-torch-hover text-white font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-all hover:shadow-[0_0_36px_rgba(255,85,0,0.5)]">
                  Book online now
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a href={BUSINESS.whatsapp} target="_blank" rel="noopener noreferrer" data-testid="town-hero-whatsapp-btn" className="border border-neutral-700 hover:border-torch text-neutral-200 hover:text-torch font-display font-bold uppercase tracking-wide px-8 py-4 rounded-sm flex items-center gap-3 transition-colors">
                  <MessageCircle className="w-4 h-4" /> WhatsApp us
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.75}>
              <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4">
                <div className="flex items-center gap-2.5"><ShieldCheck className="w-4 h-4 text-torch" /><span className="font-mono text-xs tracking-wider text-neutral-400 uppercase">Gas Safe #{BUSINESS.gasSafe}</span></div>
                <div className="flex items-center gap-2.5"><Clock className="w-4 h-4 text-torch" /><span className="font-mono text-xs tracking-wider text-neutral-400 uppercase">24/7 emergency response</span></div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-16 lg:py-20 bg-[#0C0C0C] border-y border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Reveal>
                <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white">
                  Fixed prices in {town.name}
                </h2>
              </Reveal>
              <div className="mt-8 divide-y divide-neutral-800 border-y border-neutral-800">
                {SERVICES.map((s, i) => (
                  <Reveal key={s.id} delay={i * 0.05}>
                    <Link to="/#booking" data-testid={`town-service-${s.id}`} className="group flex items-center justify-between gap-4 py-4 hover:bg-white/[0.02] px-2 transition-colors">
                      <div className="flex items-center gap-3">
                        <Check className="w-4 h-4 text-torch shrink-0" strokeWidth={3} />
                        <span className="font-display font-semibold text-neutral-200 group-hover:text-white transition-colors">{s.name}</span>
                      </div>
                      <span className="font-mono font-bold text-torch shrink-0">{s.priceLabel}</span>
                    </Link>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.2}>
                <p className="mt-6 text-sm text-neutral-500 leading-relaxed">
                  Plus new boiler installations from £1,799 fully fitted — get a free fixed quote for {town.name}.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15}>
              <div className="relative z-0 border border-neutral-800 rounded-sm overflow-hidden lg:sticky lg:top-28">
                <MapContainer center={town.coords} zoom={13} scrollWheelZoom={false} className="h-[300px] lg:h-[400px] w-full">
                  <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  <Marker position={town.coords} icon={pin} />
                </MapContainer>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
            <p className="font-mono text-xs tracking-[0.25em] text-neutral-500 uppercase mb-5">Also covering</p>
            <div className="flex flex-wrap gap-2">
              {others.map((t) => (
                <Link key={t.slug} to={`/areas/${t.slug}`} data-testid={`town-link-${t.slug}`} className="font-mono text-[10px] tracking-wider uppercase text-neutral-400 border border-neutral-800 px-3 py-1.5 rounded-sm hover:border-torch hover:text-torch transition-colors">
                  {t.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
      <WhatsAppButton />
    </div>
  );
}
